import { SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  OutputEmitterRef,
  Signal,
  ViewChild,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { Observable, combineLatest, tap, throttleTime } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

import { SEARCH_EVENT_MIN_INTERVAL_TIME, TypedTemplateDirective } from '@core/shared/util';
import { AutocompletePanelComponent, FilterSearchFieldComponent, SelectOption } from '@core/ui';
import { TreeFacet } from '@mp/shared/facets/domain';

import { TreeFacetAutocompleteOptionComponent } from './tree-facet-autocomplete-option/tree-facet-autocomplete-option.component';

function* filterBuckets<TData>(
  buckets: Iterable<TreeFacet.Bucket<TData>>,
  searchTerm: string,
): Iterable<TreeFacet.Bucket<TData>> {
  searchTerm = searchTerm.trim().toLowerCase();

  for (const bucket of buckets) {
    if (
      !searchTerm ||
      bucket.label.trim().toLowerCase().includes(searchTerm) ||
      bucket.value.trim().toLowerCase().includes(searchTerm)
    ) {
      yield bucket;
    }
  }
}

@Component({
  selector: 'mp-tree-facet-autocomplete',
  templateUrl: './tree-facet-autocomplete.component.html',
  styleUrl: './tree-facet-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SlicePipe,

    FilterSearchFieldComponent,
    AutocompletePanelComponent,
    TypedTemplateDirective,
    TreeFacetAutocompleteOptionComponent,
  ],
})
export class TreeFacetAutocompleteComponent<TData extends object | unknown = unknown> {
  readonly searchFieldPlaceholder: InputSignal<string> = input<string>('Merkmale durchsuchen');

  readonly allBuckets: InputSignal<readonly TreeFacet.Bucket<TData>[]> =
    input.required<readonly TreeFacet.Bucket<TData>[]>();

  readonly bucketSelected: OutputEmitterRef<TreeFacet.Bucket<TData>> = output<TreeFacet.Bucket<TData>>();

  readonly searchFieldUse: OutputEmitterRef<void> = output<void>();

  protected readonly searchControl = new UntypedFormControl('');

  protected readonly filteredBucketsOptions: Signal<SelectOption<TreeFacet.Bucket<TData>>[]> = toSignal(
    this.getFilteredBucketsOptionsObservable(),
    { initialValue: [] },
  );

  @ViewChild('searchField')
  protected searchField?: FilterSearchFieldComponent;

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        throttleTime(SEARCH_EVENT_MIN_INTERVAL_TIME),
        tap(() => this.searchFieldUse.emit()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  onBucketSelected(selectedBucket: TreeFacet.Bucket<TData>): void {
    this.bucketSelected.emit(selectedBucket);
  }

  focusSearchField(): void {
    this.searchField?.focus();
  }

  private getFilteredBucketsOptionsObservable(): Observable<SelectOption<TreeFacet.Bucket<TData>>[]> {
    return combineLatest([
      toObservable(this.allBuckets),
      this.searchControl.valueChanges.pipe(startWith(''), debounceTime(250)),
    ]).pipe(
      map(([buckets, value]) => {
        return value ? Array.from(filterBuckets(buckets, value)) : [];
      }),
      map((buckets) => this.mapFilteredBucketsOptions(buckets)),
    );
  }

  private mapFilteredBucketsOptions(buckets: TreeFacet.Bucket<TData>[]): SelectOption<TreeFacet.Bucket<TData>>[] {
    return buckets.map((bucket) => ({
      label: bucket.label,
      value: bucket,
    }));
  }
}
